/* eslint-disable @typescript-eslint/no-invalid-void-type */
import { ContentLegalWebservice } from '../content-api/content-legal.webservice';
import { Options } from '../options.interface';
import { CONSENT_API_CONFIG } from './consent-api.config';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ArticleTypes, ConsentInterface } from '@wizbii/utils/models';
import { Observable, combineLatest, from, of, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable()
export class ConsentWebservice {
  readonly #http = inject(HttpClient);
  readonly #consentApiConfig = inject(CONSENT_API_CONFIG);
  readonly #contentLegalWebservice = inject(ContentLegalWebservice);

  readonly #baseUrl = this.#consentApiConfig.baseUrl;
  readonly #appId = this.#consentApiConfig.appId;

  getAll(
    userId?: string,
    token?: string,
    appId?: string,
    options?: Options
  ): Observable<{ [key: string]: boolean } | null> {
    if (!userId) {
      return of(null);
    }

    if (!token) {
      return this.#http.get<{ [key: string]: boolean }>(
        `${this.#baseUrl}/v2/consent/${userId}/${appId ? appId : this.#appId}`,
        {
          ...options,
          withCredentials: true,
        }
      );
    }

    return this.#http.get<{ [key: string]: boolean }>(`${this.#baseUrl}/v2/consent/${userId}/${this.#appId}`, {
      ...options,
      withCredentials: true,
      headers: {
        'x-auth-token': token,
        'x-auth-type': 'jwt',
      },
    });
  }

  transformToMember(visitorId?: string, token?: string, options?: Options): Observable<void> {
    if (!token || token.trim() === '') {
      return throwError(() => new Error('User not connected'));
    }

    return this.#http.post<void>(`${this.#baseUrl}/v2/consent/transform/${visitorId}`, null, {
      ...options,
      withCredentials: true,
      headers: {
        'x-auth-token': token,
        'x-auth-type': 'jwt',
      },
    });
  }

  sendLastVersionsConsent(value: boolean, type: ArticleTypes, options?: Options): Observable<ConsentInterface> {
    const service = type === ArticleTypes.CGU ? 'global' : this.#appId;
    return this.#contentLegalWebservice.getVersionsByServiceAndType(service, type, options).pipe(
      switchMap((version) =>
        from(
          (window as any).WizbiiGdpr.saveConsent({
            key: type,
            value,
            lastVersion: version && version.lastVersion ? version.lastVersion : null,
          })
        )
      ),
      map((consent) => consent as ConsentInterface)
    );
  }

  sendLastVersionsConsents(
    consents: { value: boolean; type: ArticleTypes }[],
    options?: Options
  ): Observable<ConsentInterface[]> {
    return combineLatest(
      consents.map(({ type }) =>
        this.#contentLegalWebservice.getVersionsByServiceAndType(
          type === ArticleTypes.CGU ? 'global' : this.#appId,
          type,
          options
        )
      )
    ).pipe(
      switchMap((versions: { lastVersion: string }[]) => {
        const consentsToSend: ConsentInterface[] = consents.map(({ type: key, value }, index) => ({
          key,
          value,
          lastVersion: versions[index] && versions[index].lastVersion ? versions[index].lastVersion : undefined,
        }));

        return from((window as any).WizbiiGdpr.WizbiiGdpr.saveAllConsents(consentsToSend)).pipe(
          map(() => consentsToSend)
        );
      })
    );
  }

  sendAllConsentsWithLastVersionsConsent(
    consents: ConsentInterface[],
    type: ArticleTypes,
    value: boolean,
    options?: Options
  ): Observable<any> {
    const service = type === ArticleTypes.CGU ? 'global' : this.#appId;
    return this.#contentLegalWebservice.getVersionsByServiceAndType(service, type, options).pipe(
      switchMap((version) =>
        from(
          (window as any).WizbiiGdpr.WizbiiGdpr.saveAllConsents([
            ...consents,
            {
              key: type,
              value,
              lastVersion: version && version.lastVersion ? version.lastVersion : null,
            },
          ])
        )
      )
    );
  }
}
